import React, { useEffect, useState } from 'react';
import Award from 'assets/images/profile/award.png';
import { AWARDS } from 'utils/awardsConfig';

export default function AwardsModal({ isOpen, onClose, userAwards }) {
    const [isVisible, setIsVisible] = useState(false);
    const [render, setRender] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setRender(true);
            // Small delay to ensure render happens before opacity transition
            const timer = setTimeout(() => setIsVisible(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
            const timer = setTimeout(() => setRender(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
        }
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            onClose();
            // Don't setRender(false) here, let the prop change trigger the effect
        }, 300);
    };

    if (!render) return null;

    const earnedAwardIds = new Set((userAwards || []).map(a => a.id));

    // Sort: Earned first, then by original order (or any other criteria)
    const earnedAwards = AWARDS.filter(a => earnedAwardIds.has(a.id));
    const lockedAwards = AWARDS.filter(a => !earnedAwardIds.has(a.id));

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4`}
            onClick={handleClose}
        >
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/40 transition-all duration-300 ${isVisible ? 'opacity-100 backdrop-blur-sm' : 'opacity-0 backdrop-blur-none'}`}
            />

            {/* Modal Content */}
            <div
                className={`bg-background-pop w-full max-w-4xl max-h-[85vh] rounded-3xl shadow-2xl border-2 border-border flex flex-col overflow-hidden relative transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'} `}
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6 border-b border-border flex justify-between items-center bg-background-pop sticky top-0 z-10">
                    <div>
                        <h2 className="text-3xl font-bold text-text">Your Awards</h2>
                        <p className="text-text-light text-base mt-1">Keep learning to unlock more!</p>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-background rounded-full transition-colors group">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-light group-hover:text-text transition-colors">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="p-6 md:p-8 overflow-y-auto">
                    {/* Earned Section */}
                    {earnedAwards.length > 0 && (
                        <div className="mb-8">
                            <h3 className="text-xl font-bold text-text mb-4 flex items-center gap-2">
                                <span className="text-2xl">🏆</span> Unlocked
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {earnedAwards.map((awardConfig) => {
                                    const earnedData = (userAwards || []).find(a => a.id === awardConfig.id);
                                    return (
                                        <AwardCard
                                            key={awardConfig.id}
                                            award={awardConfig}
                                            isEarned={true}
                                            earnedData={earnedData}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Locked Section */}
                    {lockedAwards.length > 0 && (
                        <div>
                            <h3 className="text-xl font-bold text-text-light mb-4 flex items-center gap-2">
                                <span className="text-2xl">🔒</span> Locked
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {lockedAwards.map((awardConfig) => (
                                    <AwardCard
                                        key={awardConfig.id}
                                        award={awardConfig}
                                        isEarned={false}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function AwardCard({ award, isEarned, earnedData }) {
    return (
        <div
            className={`relative flex flex-col items-center p-4 rounded-3xl border-2 transition-all duration-300 ${isEarned
                ? 'bg-background border-primary/30 shadow-lg scale-100'
                : 'bg-background/50 border-transparent opacity-60 grayscale hover:opacity-100 hover:grayscale-[0.5] select-none'
                } `}
        >
            <div className={`w-40 h-40 mb-2 flex items-center justify-center rounded-full ${isEarned ? 'bg-primary/10' : 'bg-border/50'} `}>
                <img
                    src={award.icon === 'default' ? Award : award.icon}
                    alt={award.title}
                    className="w-32 h-32 object-contain drop-shadow-md select-none"
                    draggable={false}
                />
            </div>
            <h3 className={`text-lg font-bold text-center mb-1 leading-tight ${isEarned ? 'text-text' : 'text-text-light'} `}>
                {award.title}
            </h3>
            <p className="text-xs text-center text-text-light leading-snug mb-2 line-clamp-2">
                {award.description}
            </p>
            {isEarned && earnedData?.date && (
                <span className="text-[10px] font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-full mt-auto">
                    {new Date(earnedData.date).toLocaleDateString()}
                </span>
            )}
            {!isEarned && (
                <div className="mt-auto pt-1">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-light">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                </div>
            )}
        </div>
    );
}
